import { useEffect, useState, useCallback } from 'react';

// Signature Aviation Brand Colors
const BRAND_COLORS = {
  runway: '#161D25',      // Dark navy/black
  midnightSky: '#152644', // Deep blue
  landing: '#ECF2FF',     // Light blue/white
  altitude: '#FFFFFF',    // White
  wingspan: '#354646',    // Dark teal
  flightPlan: '#63647D',  // Gray
  jet: '#10DC9E',         // Bright teal/green (accent)
};

interface FireUpAnimationProps {
  isActive: boolean;
  onComplete?: () => void;
}

/**
 * "Fire up" animation that launches from bottom to top when AI Assistant activates.
 * Creates a dramatic aviation-themed takeoff effect using CSS animations.
 */
export default function FireUpAnimation({ isActive, onComplete }: FireUpAnimationProps) {
  const [showAnimation, setShowAnimation] = useState(false);
  const [particles, setParticles] = useState<Array<{ id: number; x: number; delay: number; size: number }>>([]);

  const handleAnimationEnd = useCallback(() => {
    setShowAnimation(false);
    onComplete?.();
  }, [onComplete]);

  useEffect(() => {
    if (isActive) {
      setShowAnimation(true);
      // Generate random particles for the fire effect
      setParticles(
        Array.from({ length: 20 }, (_, i) => ({
          id: i,
          x: Math.random() * 100, // 0 to 100%
          delay: Math.random() * 0.3,
          size: 4 + Math.random() * 8,
        }))
      );

      // Hide animation after it completes
      const timer = setTimeout(() => {
        handleAnimationEnd();
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, [isActive, handleAnimationEnd]);

  if (!showAnimation) return null;

  return (
    <div 
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 99999 }}
    >
      {/* Main energy wave rising from bottom */}
      <div
        className="absolute bottom-0 left-0 right-0 h-full animate-fire-up-wave"
        style={{
          background: `linear-gradient(to top, 
            ${BRAND_COLORS.jet}90 0%,
            ${BRAND_COLORS.midnightSky}70 30%,
            ${BRAND_COLORS.runway}50 60%,
            transparent 100%
          )`,
        }}
      >
        {/* Bright leading edge */}
        <div 
          className="absolute top-0 left-0 right-0 h-6"
          style={{
            background: `linear-gradient(to top, 
              ${BRAND_COLORS.jet} 0%,
              ${BRAND_COLORS.landing} 50%,
              transparent 100%
            )`,
            boxShadow: `0 0 40px ${BRAND_COLORS.jet}, 0 0 80px ${BRAND_COLORS.jet}`,
          }}
        />
      </div>

      {/* Center beam */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 animate-fire-up-beam"
        style={{
          background: `linear-gradient(to top, ${BRAND_COLORS.jet}, ${BRAND_COLORS.landing}, transparent)`,
          boxShadow: `0 0 20px ${BRAND_COLORS.jet}, 0 0 40px ${BRAND_COLORS.midnightSky}`,
        }}
      />

      {/* Expanding ring effect */}
      <div
        className="absolute bottom-20 left-1/2 -translate-x-1/2 animate-fire-up-ring"
      >
        <div 
          className="w-32 h-32 rounded-full border-4"
          style={{
            borderColor: BRAND_COLORS.jet,
            boxShadow: `0 0 30px ${BRAND_COLORS.jet}, inset 0 0 30px ${BRAND_COLORS.jet}50`,
          }}
        />
      </div>

      {/* Particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute bottom-0 rounded-full animate-fire-up-particle"
          style={{
            left: `${particle.x}%`,
            width: particle.size,
            height: particle.size,
            background: particle.id % 2 === 0 ? BRAND_COLORS.jet : BRAND_COLORS.landing,
            boxShadow: `0 0 10px ${BRAND_COLORS.jet}`,
            animationDelay: `${particle.delay}s`,
          }}
        />
      ))}

      {/* Side trails */}
      <div
        className="absolute bottom-0 w-0.5 h-screen animate-fire-up-trail"
        style={{ 
          left: '40%',
          background: `linear-gradient(to top, ${BRAND_COLORS.midnightSky}, transparent)`,
        }}
      />
      <div
        className="absolute bottom-0 w-0.5 h-screen animate-fire-up-trail"
        style={{ 
          left: '60%',
          background: `linear-gradient(to top, ${BRAND_COLORS.midnightSky}, transparent)`,
          animationDelay: '0.1s',
        }}
      />

      {/* Bottom flash */}
      <div
        className="absolute bottom-0 left-0 right-0 h-40 animate-fire-up-flash"
        style={{
          background: `radial-gradient(ellipse at bottom center, 
            ${BRAND_COLORS.jet}70 0%, 
            ${BRAND_COLORS.midnightSky}40 50%, 
            transparent 100%
          )`,
        }}
      />

      {/* CSS Keyframes */}
      <style>{`
        @keyframes fire-up-wave {
          0% { transform: translateY(100%); opacity: 1; }
          70% { opacity: 0.8; }
          100% { transform: translateY(-100%); opacity: 0; }
        }
        
        @keyframes fire-up-beam {
          0% { height: 0; opacity: 0; }
          30% { opacity: 1; }
          70% { opacity: 0.8; }
          100% { height: 100vh; opacity: 0; }
        }
        
        @keyframes fire-up-ring {
          0% { transform: translateX(-50%) scale(0); opacity: 1; }
          100% { transform: translateX(-50%) scale(4); opacity: 0; }
        }
        
        @keyframes fire-up-particle {
          0% { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(-100vh) scale(0); opacity: 0; }
        }
        
        @keyframes fire-up-trail {
          0% { transform: translateY(100%); opacity: 0; }
          30% { opacity: 0.6; }
          100% { transform: translateY(-100%); opacity: 0; }
        }
        
        @keyframes fire-up-flash {
          0% { opacity: 0; }
          30% { opacity: 1; }
          100% { opacity: 0; }
        }
        
        .animate-fire-up-wave {
          animation: fire-up-wave 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        
        .animate-fire-up-beam {
          animation: fire-up-beam 0.6s ease-out forwards;
        }
        
        .animate-fire-up-ring {
          animation: fire-up-ring 0.8s ease-out forwards;
        }
        
        .animate-fire-up-particle {
          animation: fire-up-particle 0.8s ease-out forwards;
        }
        
        .animate-fire-up-trail {
          animation: fire-up-trail 0.7s ease-out forwards;
        }
        
        .animate-fire-up-flash {
          animation: fire-up-flash 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
